<template>
  <div v-if="customer">
    <Flex
      direction="column"
      :gap=".5"
      :class="$style.alerts"
    >
      <BaseAlert
        v-if="customer?.blocked"
        :text="$t('customers.blocked_customer')"
        color="error"
        v-test="'customerBlockedLabel'"
        size="s"
      />
      <BaseAlert
        v-if="requiresFullPrepayment(customer)"
        :text="$t('customers.prepayment_required_customer')"
        color="warning"
        v-test="'customerPrepaymentRequiredLabel'"
        size="s"
      />
      <Segments />
    </Flex>
    <Flex
      :direction="$screen === 's' ? 'column' : 'row'"
      :equalWidth="$screen === 's'"
    >
      <Flex
        direction="column"
        :class="$style.leftCards"
      >
        <BaseText
          :routerLink="customerSegmentReferrer ? {
            name: 'customers-segments',
            params: {
              key: underscoreToDash(customerSegmentReferrer),
            },
          } : {
            name: 'customers'
          }"
          iconBefore="arrow-left"
          v-test="'btn-back'"
        >
          {{ $t('customers.back_to_customers') }}
        </BaseText>
        <Actions :summary="summary" />
        <Details :customer="customer" />
        <Settings
          :customer="customer"
          @refresh="getHistory"
        />
      </Flex>
      <Flex
        direction="column"
        :class="$style.rightCards"
      >
        <Stats
          v-if="summary"
          :summary="summary"
        />
        <BaseSpinner
          v-else
          inline
        />
        <Flex
          equalWidth
          :direction="$screen !== 'l' ? 'column' : 'row'"
        >
          <Flex
            :class="[$screen === 's' ? $style.order2 : '']"
            equalWidth
          >
            <History
              :history="history"
              @refresh="getHistory"
            />
          </Flex>
          <Flex
            direction="column"
            :class="[$screen === 's' ? $style.order1 : '']"
          >
            <CustomerTags
              v-model="customerTagIds"
              saveImmediately
            />
            <Notes @refresh="getHistory" />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
    <router-view @updated="onUpdated" />
  </div>
</template>

<script lang="ts">
export default {
  name: 'CustomerOverview',
};
</script>

<script
  lang="ts"
  setup
>
import { useQuery } from '@vue/apollo-composable';
import { useSessionStorage } from '@vueuse/core';
import { computed, inject, ref, watch } from 'vue';
import axios from '@/axios';
import { underscoreToDash } from '@/helpers/formatting';
import CustomerTags from '../../CustomerTags.vue';
import { requiresFullPrepayment } from '../prepaymentRequirement';
import Actions from './Actions.vue';
import Details from './Details.vue';
import { GET_CUSTOMER_SUMMARY } from './graphql';
import History from './History.vue';
import Notes from './Notes.vue';
import Segments from './Segments.vue';
import Settings from './Settings.vue';
import Stats from './Stats.vue';

const customer = inject<any>('customer');
const customerId = computed(() => customer?.value?.id || null);
const summary = ref();

const customerTags = computed(() => customer?.value?.customerTags || []);
const customerTagIds = ref([]);

watch(
  customerTags,
  () => {
    customerTagIds.value = customerTags.value?.map((tag: any) => tag.id) || [];
  },
  {
    immediate: true,
    deep: true,
  },
);

const customerSegmentReferrer = useSessionStorage(
  'customer-segment-referrer',
  null,
);

const getCustomerSummary = () => {
  const { onResult } = useQuery(GET_CUSTOMER_SUMMARY, {
    id: customerId.value,
  });

  onResult(({ data: { customerSummary } }) => {
    summary.value = customerSummary;
  });
};

watch(
  customerId,
  (customerId) => {
    if (customerId) {
      getCustomerSummary();
    }
  },
  {
    immediate: true,
  },
);

const history = ref([]);
const getHistory = () => {
  axios
    .get(`/customers/${customerId.value}/timeline_items`)
    .then((response) => {
      history.value = response.data.timeline_items;
    });
};

watch(
  customerId,
  (customerId) => {
    if (customerId) {
      getHistory();
    }
  },
  {
    immediate: true,
  },
);

const emit = defineEmits(['updated']);

const onUpdated = () => {
  emit('updated');
};
</script>

<style
  lang="scss"
  module
>
.leftCards {
  width: 300px;
}

.rightCards {
  width: calc(100% - 300px);
}

.alerts {
  margin-bottom: $spacing;
}

.order2 {
  order: 2;
}

.order1 {
  order: 1;
}
</style>
